import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../utils/api';
import { formatCurrency } from '../utils/helpers';

const ACCENT = '#c88346';
// Compact day for card footers (e.g. "15 Sep 26"); "—" when absent.
const fmtDay = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
};
// Per-level badge colors so the discount scope reads at a glance.
const LEVEL_STYLES = {
  order:       { bg: '#eef2ff', fg: '#4f46e5' },
  products:    { bg: '#ecfdf5', fg: '#047857' },
  collections: { bg: '#fdf2f8', fg: '#be185d' },
  custom:      { bg: '#fff7ed', fg: '#c2410c' },
};
const levelStyle = (lvl) => LEVEL_STYLES[lvl] || { bg: '#f1f5f9', fg: '#475569' };
const inputStyle = { padding: '9px 11px', border: '2px solid rgba(200,131,70,0.18)', borderRadius: '10px', fontSize: '13px', fontWeight: 500, outline: 'none', boxSizing: 'border-box', width: '100%' };
const labelStyle = { fontSize: '11px', fontWeight: 700, color: '#8b7d6e', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px', display: 'block' };
const btn = (bg, color = '#fff') => ({ padding: '8px 16px', background: bg, color, border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' });

const emptyForm = () => ({
  id: null, code: '', name: '', level: 'order', type: 'percentage',
  value: '', slab: '', view: true, isActive: true, combinable: true, applyOnMrp: false,
  allLocations: false, stores: [],
  products: [], collections: [], conditions: []
});

// Multi-select for stores (static list, client-side filter).
function StorePicker({ stores, selected, onChange }) {
  const [q, setQ] = useState('');
  const isSel = (id) => selected.some(s => s.id === id);
  const toggle = (s) => isSel(s.id) ? onChange(selected.filter(x => x.id !== s.id)) : onChange([...selected, { id: s.id, name: s.name }]);
  const filtered = stores.filter(s => (s.name || '').toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: selected.length ? '6px' : 0 }}>
        {selected.map(s => (
          <span key={s.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#fdf3e8', border: '1px solid #f0d9bf', borderRadius: '8px', padding: '3px 6px', fontSize: '11px', color: '#7a5326' }}>
            {s.name || `#${s.id}`}
            <button type="button" onClick={() => onChange(selected.filter(x => x.id !== s.id))} style={{ border: 'none', background: 'transparent', color: '#a06a2f', cursor: 'pointer', fontWeight: 700 }}>✕</button>
          </span>
        ))}
      </div>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search stores…" style={{ ...inputStyle, marginBottom: '4px' }} />
      <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #ece0d1', borderRadius: '10px' }}>
        {filtered.length === 0 && <div style={{ padding: '8px 10px', fontSize: '12px', color: '#9c8b78' }}>No stores</div>}
        {filtered.map(s => (
          <div key={s.id} onClick={() => toggle(s)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', fontSize: '12px', cursor: 'pointer', borderBottom: '1px solid #f5efe7', background: isSel(s.id) ? '#fdf3e8' : '#fff' }}>
            <input type="checkbox" checked={isSel(s.id)} readOnly style={{ pointerEvents: 'none', accentColor: ACCENT }} />
            <span style={{ flex: 1, color: '#3d2b1f' }}>{s.name}</span>
            <span style={{ fontSize: '10px', color: '#9c8b78' }}>{s.type}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Debounced search picker for variants or collections. `selected` is [{id,title}].
function EntityPicker({ kind, selected, onChange, placeholder }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        if (kind === 'variant') {
          const r = await api.get('/manage/discounts/variant-search', { params: { q } });
          setResults((r.data.variants || []).map(v => ({ id: String(v.variantId), title: v.title, productId: String(v.productId), productTitle: String(v.title || '').split(' — ')[0], sub: `${v.sku || ''}${v.productType ? ' · ' + v.productType : ''} · ${formatCurrency(v.price)}` })));
        } else {
          const r = await api.get('/manage/discounts/collections', { params: { q } });
          setResults((r.data.collections || []).map(c => ({ id: String(c.id), title: c.title, sub: c.handle })));
        }
        setOpen(true);
      } catch { setResults([]); }
      setLoading(false);
    }, 300);
    return () => clearTimeout(t);
  }, [q, kind]);

  const isSelected = (id) => selected.some(s => s.id === id);
  // Toggle selection WITHOUT closing the dropdown, so several variants can be ticked in a row.
  const toggle = (item) => {
    if (isSelected(item.id)) onChange(selected.filter(s => s.id !== item.id));
    else onChange([...selected, { id: item.id, title: item.title }]);
  };
  const remove = (id) => onChange(selected.filter(s => s.id !== id));

  // A product is "all selected" when every variant shown for it is selected.
  const productAllSelected = (g) => g.variants.length > 0 && g.variants.every(v => isSelected(v.id));
  // The product checkbox: tick → select all its variants; untick → remove them all.
  const toggleWholeProduct = (g) => {
    if (productAllSelected(g)) {
      const ids = new Set(g.variants.map(v => v.id));
      onChange(selected.filter(s => !ids.has(s.id)));
    } else {
      const existing = new Set(selected.map(s => s.id));
      const toAdd = g.variants.filter(v => !existing.has(v.id)).map(v => ({ id: v.id, title: v.title }));
      onChange([...selected, ...toAdd]);
    }
  };

  // Group variant results by product (for the product-level checkbox). Collections stay flat.
  const variantGroups = () => {
    const map = new Map();
    results.forEach(r => {
      if (!map.has(r.productId)) map.set(r.productId, { productId: r.productId, productTitle: r.productTitle, variants: [] });
      map.get(r.productId).variants.push(r);
    });
    return [...map.values()];
  };

  // Close the dropdown only when clicking outside the picker.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: selected.length ? '6px' : 0 }}>
        {selected.map(s => (
          <span key={s.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#fdf3e8', border: '1px solid #f0d9bf', borderRadius: '8px', padding: '3px 6px', fontSize: '11px', color: '#7a5326' }}>
            {s.title || `#${s.id}`}
            <button onClick={() => remove(s.id)} style={{ border: 'none', background: 'transparent', color: '#a06a2f', cursor: 'pointer', fontWeight: 700 }}>✕</button>
          </span>
        ))}
      </div>
      <input
        value={q} onChange={e => setQ(e.target.value)} placeholder={placeholder || `Search ${kind}s…`}
        style={inputStyle} onFocus={() => results.length && setOpen(true)}
      />
      {open && (loading || results.length > 0) && (
        <div style={{ position: 'absolute', zIndex: 20, left: 0, right: 0, background: '#fff', border: '1px solid #ece0d1', borderRadius: '10px', marginTop: '2px', maxHeight: '440px', overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}>
          {loading && <div style={{ padding: '8px 10px', fontSize: '12px', color: '#999' }}>Searching…</div>}
          {kind === 'variant' ? (
            variantGroups().map(g => (
              <div key={`g-${g.productId}`}>
                <div onClick={() => toggleWholeProduct(g)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', background: '#faf7f3', borderBottom: '1px solid #f0e8dd', cursor: 'pointer' }}>
                  <input type="checkbox" checked={productAllSelected(g)} readOnly style={{ pointerEvents: 'none', accentColor: ACCENT, width: '15px', height: '15px', flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: '11px', fontWeight: 700, color: '#7a5326', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.productTitle} <span style={{ fontWeight: 400, color: '#9c8b78' }}>· whole product ({g.variants.length})</span></span>
                </div>
                {g.variants.map(r => {
                  const sel = isSelected(r.id);
                  const rowLabel = r.title.includes(' — ') ? r.title.split(' — ').slice(1).join(' — ') : r.title;
                  return (
                    <div key={r.id} onClick={() => toggle(r)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px 7px 18px', fontSize: '12px', cursor: 'pointer', borderBottom: '1px solid #f5efe7', background: sel ? '#fdf3e8' : '#fff' }}
                      onMouseEnter={e => e.currentTarget.style.background = sel ? '#f8e8d3' : '#faf6f1'} onMouseLeave={e => e.currentTarget.style.background = sel ? '#fdf3e8' : '#fff'}>
                      <input type="checkbox" checked={sel} readOnly style={{ pointerEvents: 'none', accentColor: ACCENT, width: '15px', height: '15px', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: '#3d2b1f' }}>{rowLabel}</div>
                        {r.sub && <div style={{ fontSize: '10px', color: '#9c8b78' }}>{r.sub}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          ) : (
            results.map(r => {
              const sel = isSelected(r.id);
              return (
                <div key={r.id} onClick={() => toggle(r)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', fontSize: '12px', cursor: 'pointer', borderBottom: '1px solid #f5efe7', background: sel ? '#fdf3e8' : '#fff' }}
                  onMouseEnter={e => e.currentTarget.style.background = sel ? '#f8e8d3' : '#faf6f1'} onMouseLeave={e => e.currentTarget.style.background = sel ? '#fdf3e8' : '#fff'}>
                  <input type="checkbox" checked={sel} readOnly style={{ pointerEvents: 'none', accentColor: ACCENT, width: '15px', height: '15px', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: '#3d2b1f' }}>{r.title}</div>
                    {r.sub && <div style={{ fontSize: '10px', color: '#9c8b78' }}>{r.sub}</div>}
                  </div>
                </div>
              );
            })
          )}
          {!loading && results.length > 0 && (
            <div style={{ position: 'sticky', bottom: 0, background: '#faf7f3', borderTop: '1px solid #ece0d1', padding: '6px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '10px', color: '#9c8b78' }}>{selected.length} selected</span>
              <button type="button" onClick={() => setOpen(false)} style={{ ...btn(ACCENT), padding: '4px 12px', fontSize: '11px' }}>Done</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Discounts() {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null = list view; object = editing/creating
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [allStores, setAllStores] = useState([]);
  // Super-admin-granted permissions for THIS user. Read is always allowed; write/delete gate the
  // Create/Edit/Delete controls. Default false until loaded (so buttons don't flash before the check).
  const [perms, setPerms] = useState({ canWrite: false, canDelete: false, superAdmin: false });
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive
  const [levelFilter, setLevelFilter] = useState('all');   // all | order | products | collections | custom

  useEffect(() => {
    api.get('/manage/discounts/stores').then(r => setAllStores(r.data.stores || [])).catch(() => {});
    api.get('/manage/discounts/my-permissions')
      .then(r => setPerms({ canWrite: !!r.data.canWrite, canDelete: !!r.data.canDelete, superAdmin: !!r.data.superAdmin }))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/manage/discounts');
      setDiscounts(r.data.discounts || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load discounts');
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const startCreate = () => { setError(''); setForm(emptyForm()); };
  const startEdit = async (d, readOnly = false) => {
    setError('');
    // Open immediately with ID placeholders, then resolve real titles below.
    setForm({
      readOnly, // view-only managers open the page read-only (no Save; all fields disabled)
      id: d.id, code: d.code, name: d.name || '', level: d.level, type: d.type,
      value: String(d.value), slab: String(d.slab), view: d.view, isActive: d.isActive, combinable: d.combinable,
      applyOnMrp: !!d.applyOnMrp,
      allLocations: !!d.allLocations,
      stores: (d.locationIds || []).map(id => ({ id: String(id), name: (allStores.find(s => s.id === String(id)) || {}).name || `Store #${id}` })),
      products: (d.productVariantIds || []).map(id => ({ id: String(id), title: `Variant #${id}` })),
      collections: (d.collectionIds || []).map(id => ({ id: String(id), title: `Collection #${id}` })),
      conditions: (d.conditions || []).map(c => ({
        name: c.name || '', minQuantity: String(c.minQuantity || 1), allocPref: c.allocPref || 'cheapest',
        variants: (c.variantIds || []).map(id => ({ id: String(id), title: `Variant #${id}` })),
        collections: (c.collectionIds || []).map(id => ({ id: String(id), title: `Collection #${id}` }))
      }))
    });

    // Resolve variant/collection IDs → display titles and relabel the chips.
    const allVarIds = [...(d.productVariantIds || []), ...(d.conditions || []).flatMap(c => c.variantIds || [])].map(String);
    const allColIds = [...(d.collectionIds || []), ...(d.conditions || []).flatMap(c => c.collectionIds || [])].map(String);
    if (!allVarIds.length && !allColIds.length) return;
    try {
      const r = await api.post('/manage/discounts/resolve-refs', { variantIds: allVarIds, collectionIds: allColIds });
      const vMap = new Map((r.data.variants || []).map(v => [String(v.id), v.title]));
      const cMap = new Map((r.data.collections || []).map(c => [String(c.id), c.title]));
      const relabel = (arr, map) => arr.map(x => ({ ...x, title: map.get(String(x.id)) || x.title }));
      setForm(f => (f && f.id === d.id) ? {
        ...f,
        products: relabel(f.products, vMap),
        collections: relabel(f.collections, cMap),
        conditions: f.conditions.map(c => ({ ...c, variants: relabel(c.variants, vMap), collections: relabel(c.collections, cMap) }))
      } : f);
    } catch (e) {
      // Non-fatal — chips keep the ID labels if resolution fails
    }
  };

  const set = (patch) => setForm(f => ({ ...f, ...patch }));
  const addCondition = () => set({ conditions: [...form.conditions, { name: '', minQuantity: '1', allocPref: 'cheapest', variants: [], collections: [] }] });
  const updateCondition = (i, patch) => set({ conditions: form.conditions.map((c, idx) => idx === i ? { ...c, ...patch } : c) });
  const removeCondition = (i) => set({ conditions: form.conditions.filter((_, idx) => idx !== i) });

  const save = async () => {
    setError('');
    const payload = {
      code: form.code, name: form.name, level: form.level, type: form.type,
      value: Number(form.value), slab: Number(form.slab || 0),
      view: form.view, isActive: form.isActive,
      applyOnMrp: !!form.applyOnMrp,
      // Product/collection discounts — and any apply-on-MRP discount — are never combinable.
      combinable: (form.applyOnMrp || form.level === 'products' || form.level === 'collections') ? false : form.combinable,
      allLocations: form.allLocations,
      locationIds: form.allLocations ? [] : form.stores.map(s => s.id),
      productVariantIds: form.products.map(p => p.id),
      collectionIds: form.collections.map(c => c.id),
      conditions: form.conditions.map(c => ({
        name: c.name, minQuantity: Number(c.minQuantity || 1), allocPref: c.allocPref || 'cheapest',
        variantIds: c.variants.map(v => v.id), collectionIds: c.collections.map(x => x.id)
      }))
    };
    setSaving(true);
    try {
      if (form.id) await api.put(`/manage/discounts/${form.id}`, payload);
      else await api.post('/manage/discounts', payload);
      setForm(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save');
    }
    setSaving(false);
  };

  const del = async (d) => {
    if (!window.confirm(`Delete discount "${d.code}"?`)) return;
    try { await api.delete(`/manage/discounts/${d.id}`); await load(); }
    catch (e) { setError(e.response?.data?.error || 'Failed to delete'); }
  };

  // Derived list state: counts for the summary + the search/filter result.
  const activeCount = useMemo(() => discounts.filter(d => d.isActive).length, [discounts]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return discounts.filter(d => {
      if (statusFilter === 'active' && !d.isActive) return false;
      if (statusFilter === 'inactive' && d.isActive) return false;
      if (levelFilter !== 'all' && d.level !== levelFilter) return false;
      if (q && !(`${d.code} ${d.name || ''}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [discounts, query, statusFilter, levelFilter]);
  const levelsPresent = useMemo(
    () => ['all', ...Array.from(new Set(discounts.map(d => d.level)))],
    [discounts]
  );

  // ---------------- List view ----------------
  if (!form) {
    const segBtn = (activeVal, val, label, count) => {
      const on = activeVal === val;
      return (
        <button key={val} onClick={() => (label === undefined ? setLevelFilter(val) : setStatusFilter(val))}
          style={{
            padding: '6px 13px', borderRadius: '999px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer',
            border: on ? `1px solid ${ACCENT}` : '1px solid #e7ddce',
            background: on ? ACCENT : '#fff', color: on ? '#fff' : '#7a6b5d',
            display: 'inline-flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', transition: 'all .12s',
          }}>
          {label ?? (val === 'all' ? 'All levels' : val)}
          {count !== undefined && (
            <span style={{ background: on ? 'rgba(255,255,255,0.28)' : '#f1ece5', color: on ? '#fff' : '#9c8b78',
              borderRadius: '999px', padding: '0 6px', fontSize: '11px', fontWeight: 700 }}>{count}</span>
          )}
        </button>
      );
    };

    return (
      <div style={{ padding: '24px', maxWidth: '1320px', margin: '0 auto' }}>
        {/* Header + summary */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '18px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: 800, color: '#3d2b1f', margin: 0 }}>Discounts</h1>
            <div style={{ fontSize: '13px', color: '#9c8b78', marginTop: '4px' }}>
              {discounts.length} total · <span style={{ color: '#067647', fontWeight: 600 }}>{activeCount} active</span> · {discounts.length - activeCount} inactive
            </div>
          </div>
          {perms.canWrite && <button onClick={startCreate} style={btn(`linear-gradient(135deg,${ACCENT} 0%,#b06e2f 100%)`)}>+ Create Discount</button>}
        </div>
        {error && <div style={{ background: '#fef2f2', color: '#dc2626', padding: '10px', borderRadius: '10px', marginBottom: '12px', fontSize: '13px' }}>{error}</div>}

        {/* Toolbar: search + status + level filters */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: '360px' }}>
            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#b9a892', fontSize: '14px' }}>⌕</span>
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search code or name…"
              style={{ ...inputStyle, paddingLeft: '32px', border: '1px solid #e7ddce', background: '#fff' }} />
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {segBtn(statusFilter, 'all', 'All')}
            {segBtn(statusFilter, 'active', 'Active')}
            {segBtn(statusFilter, 'inactive', 'Inactive')}
          </div>
          {levelsPresent.length > 2 && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {levelsPresent.map(lvl => segBtn(levelFilter, lvl, undefined))}
            </div>
          )}
        </div>

        {/* Card grid */}
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#9c8b78', fontSize: '14px' }}>Loading…</div>
        ) : discounts.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#9c8b78', fontSize: '14px', background: '#faf7f3', borderRadius: '16px', border: '1px dashed #e7ddce' }}>
            No discounts yet. Click “Create Discount”.
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#9c8b78', fontSize: '14px', background: '#faf7f3', borderRadius: '16px', border: '1px dashed #e7ddce' }}>
            No discounts match your filters.
          </div>
        ) : (
          <>
            <div style={{ fontSize: '12px', color: '#9c8b78', marginBottom: '10px' }}>Showing {filtered.length} of {discounts.length}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '14px' }}>
              {filtered.map(d => {
                const lv = levelStyle(d.level);
                const nameDiffers = d.name && d.name.toLowerCase() !== d.code.toLowerCase();
                return (
                  <div key={d.id} style={{
                    background: '#fff', borderRadius: '14px', border: '1px solid #ece0d1',
                    padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px',
                    boxShadow: '0 1px 2px rgba(61,43,31,0.04)', opacity: d.isActive ? 1 : 0.72,
                  }}>
                    {/* Header: code + status */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '15px', fontWeight: 800, color: '#3d2b1f', wordBreak: 'break-word', letterSpacing: '-0.01em' }}>{d.code}</div>
                        {nameDiffers && <div style={{ fontSize: '12px', color: '#9c8b78', marginTop: '2px', wordBreak: 'break-word' }}>{d.name}</div>}
                      </div>
                      <span style={{
                        flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 9px', borderRadius: '999px',
                        fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap',
                        background: d.isActive ? '#ecfdf3' : '#fef2f2', color: d.isActive ? '#067647' : '#b42318',
                      }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: d.isActive ? '#12b76a' : '#f04438' }} />
                        {d.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    {/* Value + level/type */}
                    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '10px' }}>
                      <div>
                        <div style={{ fontSize: '24px', fontWeight: 800, color: ACCENT, lineHeight: 1, letterSpacing: '-0.02em' }}>
                          {d.type === 'percentage' ? `${d.value}%` : formatCurrency(d.value)}
                        </div>
                        <div style={{ fontSize: '11.5px', color: '#9c8b78', marginTop: '5px' }}>
                          {d.type === 'percentage' ? 'off' : 'off'}{d.slab ? ` · min ${formatCurrency(d.slab)}` : ''}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px' }}>
                        <span style={{ background: lv.bg, color: lv.fg, padding: '3px 9px', borderRadius: '7px', fontSize: '11px', fontWeight: 700, textTransform: 'capitalize' }}>{d.level}</span>
                        <span title={d.combinable ? 'Can combine with other coupons' : 'Cannot combine with other coupons'}
                          style={{ fontSize: '10.5px', fontWeight: 600, color: d.combinable ? '#0e7490' : '#9c8b78',
                            background: d.combinable ? '#ecfeff' : '#f4ede3', padding: '2px 8px', borderRadius: '999px', whiteSpace: 'nowrap' }}>
                          {d.combinable ? 'Combinable' : 'Exclusive'}
                        </span>
                      </div>
                    </div>

                    {/* Footer: meta + actions */}
                    <div style={{ borderTop: '1px solid #f4ede3', paddingTop: '11px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                      <div style={{ fontSize: '11px', color: '#a89a89', minWidth: 0 }}>
                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.createdBy || '—'}</div>
                        <div>Updated {fmtDay(d.updatedAt)}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                        {perms.canWrite
                          ? <button onClick={() => startEdit(d)} style={{ ...btn('#fff', ACCENT), border: `1px solid ${ACCENT}`, padding: '5px 12px', fontSize: '12px' }}>Edit</button>
                          : <button onClick={() => startEdit(d, true)} style={{ ...btn('#fff', '#4f46e5'), border: '1px solid #c7d2fe', padding: '5px 12px', fontSize: '12px' }}>View</button>}
                        {perms.canDelete && <button onClick={() => del(d)} title="Delete" style={{ ...btn('#fff', '#dc2626'), border: '1px solid #fca5a5', padding: '5px 10px', fontSize: '12px' }}>Delete</button>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  }

  // ---------------- Form view ----------------
  const isCustom = form.level === 'custom';
  // Product- & collection-level discounts — and any apply-on-MRP discount — are never combinable:
  // the "Combine with other coupons" toggle is forced off and disabled for them.
  const noCombine = form.level === 'products' || form.level === 'collections' || form.applyOnMrp;
  return (
    <div style={{ padding: '24px 24px 480px', maxWidth: '860px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
        <h1 style={{ fontSize: '20px', fontWeight: 800, color: '#3d2b1f', margin: 0 }}>{form.readOnly ? 'View' : (form.id ? 'Edit' : 'Create')} Discount</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => { setForm(null); setError(''); }} style={btn('#f1ece5', '#7a6b5d')}>← Back</button>
          {!form.readOnly && <button onClick={save} disabled={saving} style={btn(saving ? '#ccc' : `linear-gradient(135deg,${ACCENT} 0%,#b06e2f 100%)`)}>{saving ? 'Saving…' : 'Save Discount'}</button>}
        </div>
      </div>
      {error && <div style={{ background: '#fef2f2', color: '#dc2626', padding: '10px', borderRadius: '10px', marginBottom: '12px', fontSize: '13px' }}>{error}</div>}

      <div style={{ background: '#fff', borderRadius: '16px', padding: '22px', border: '1px solid #ece0d1', boxShadow: '0 8px 32px rgba(26,26,46,0.06)' }}>
        {/* In read-only (view-only manager) mode, a disabled fieldset makes every field/control
            inside non-editable while still showing the full configuration. */}
        <fieldset disabled={form.readOnly} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
        {/* Basic fields */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <div><label style={labelStyle}>Code *</label><input style={inputStyle} value={form.code} onChange={e => set({ code: e.target.value.toUpperCase() })} /></div>
          <div><label style={labelStyle}>Name (internal)</label><input style={inputStyle} value={form.name} onChange={e => set({ name: e.target.value })} /></div>
          <div><label style={labelStyle}>Type</label>
            <select style={inputStyle} value={form.type} onChange={e => set({ type: e.target.value })}>
              <option value="percentage">Percentage (%)</option>
              <option value="fixed">Fixed (₹)</option>
            </select>
          </div>
          <div><label style={labelStyle}>Value {form.type === 'percentage' ? '(%)' : '(₹)'}</label><input type="number" style={inputStyle} value={form.value} onChange={e => set({ value: e.target.value })} /></div>
          <div><label style={labelStyle}>Slab (min value ₹)</label><input type="number" style={inputStyle} value={form.slab} onChange={e => set({ slab: e.target.value })} /></div>
          <div><label style={labelStyle}>Level</label>
            <select style={inputStyle} value={form.level} onChange={e => { const lvl = e.target.value; set((lvl === 'products' || lvl === 'collections') ? { level: lvl, combinable: false } : { level: lvl }); }}>
              <option value="order">Order</option>
              <option value="products">Product</option>
              <option value="collections">Collection</option>
              <option value="custom">Custom (bundle)</option>
            </select>
          </div>
        </div>

        {/* Toggles */}
        <div style={{ display: 'flex', gap: '20px', marginTop: '14px', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: noCombine ? '#b0a595' : '#3d2b1f', cursor: noCombine ? 'not-allowed' : 'pointer' }} title={noCombine ? 'This discount cannot be combined with other coupons' : ''}>
            <input type="checkbox" checked={noCombine ? false : form.combinable} disabled={noCombine} onChange={e => set({ combinable: e.target.checked })} /> Combine with other coupons
            {noCombine && <span style={{ fontSize: '11px', color: '#b0a595' }}>{form.applyOnMrp ? '(not allowed when applying on MRP)' : '(not allowed for product / collection)'}</span>}
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#3d2b1f', cursor: 'pointer' }} title="When on, the discount is calculated on the product's MRP (compare-at price) instead of the selling price. This reprices eligible lines to MRP.">
            <input type="checkbox" checked={!!form.applyOnMrp} onChange={e => set(e.target.checked ? { applyOnMrp: true, combinable: false } : { applyOnMrp: false })} /> Discount applies on MRP
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#3d2b1f', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.view} onChange={e => set({ view: e.target.checked })} /> Show in Available Coupons
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#3d2b1f', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.isActive} onChange={e => set({ isActive: e.target.checked })} /> Active
          </label>
        </div>

        {/* Stores (mandatory): all stores, or a specific set */}
        <div style={{ marginTop: '18px' }}>
          <label style={labelStyle}>Stores * <span style={{ fontWeight: 400, textTransform: 'none', color: '#9c8b78' }}>(where this coupon is valid)</span></label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#3d2b1f', cursor: 'pointer', marginBottom: '8px' }}>
            <input type="checkbox" checked={form.allLocations} onChange={e => set({ allLocations: e.target.checked })} /> All stores
          </label>
          {!form.allLocations && (
            <>
              <StorePicker stores={allStores} selected={form.stores} onChange={v => set({ stores: v })} />
              {form.stores.length === 0 && <div style={{ fontSize: '11px', color: '#b45309', marginTop: '4px' }}>Select at least one store, or tick “All stores”.</div>}
            </>
          )}
        </div>

        {/* Level-specific targeting */}
        {(form.level === 'products') && (
          <div style={{ marginTop: '18px' }}>
            <label style={labelStyle}>Eligible Variants</label>
            <EntityPicker kind="variant" selected={form.products} onChange={v => set({ products: v })} />
          </div>
        )}
        {(form.level === 'collections') && (
          <div style={{ marginTop: '18px' }}>
            <label style={labelStyle}>Eligible Collections</label>
            <EntityPicker kind="collection" selected={form.collections} onChange={v => set({ collections: v })} />
          </div>
        )}

        {/* Custom bundle conditions */}
        {isCustom && (
          <div style={{ marginTop: '18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Bundle Conditions (all must be met)</label>
              <button onClick={addCondition} style={btn('#eef2ff', '#4f46e5')}>+ Add Condition</button>
            </div>
            <div style={{ fontSize: '11px', color: '#9c8b78', marginBottom: '10px' }}>
              Discount applies when every condition is satisfied and the total of the matched items ≥ Slab (₹{form.slab || 0}). The discount % / ₹ is applied to those matched items.
            </div>
            {form.conditions.length === 0 && <div style={{ fontSize: '12px', color: '#b0a595', padding: '8px 0' }}>No conditions yet — add at least one.</div>}
            {form.conditions.map((c, i) => (
              <div key={i} style={{ border: '1px solid #ece0d1', borderRadius: '12px', padding: '14px', marginBottom: '10px', background: '#faf7f3' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: ACCENT }}>Condition {i + 1}</span>
                  <button onClick={() => removeCondition(i)} style={{ ...btn('#fff', '#dc2626'), border: '1px solid #fca5a5', padding: '3px 10px', fontSize: '12px' }}>Remove</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px', marginBottom: '10px' }}>
                  <div><label style={labelStyle}>Name (internal)</label><input style={inputStyle} value={c.name} placeholder="e.g. Mattress / Accessories" onChange={e => updateCondition(i, { name: e.target.value })} /></div>
                  <div><label style={labelStyle}>Min Quantity</label><input type="number" min="1" style={inputStyle} value={c.minQuantity} onChange={e => updateCondition(i, { minQuantity: e.target.value })} /></div>
                </div>
                <label style={labelStyle}>Variants</label>
                <EntityPicker kind="variant" selected={c.variants} onChange={v => updateCondition(i, { variants: v })} />
                <div style={{ height: '10px' }} />
                <label style={labelStyle}>…or whole Collections</label>
                <EntityPicker kind="collection" selected={c.collections} onChange={v => updateCondition(i, { collections: v })} />
              </div>
            ))}
          </div>
        )}

        </fieldset>
        <div style={{ display: 'flex', gap: '10px', marginTop: '22px', justifyContent: 'flex-end' }}>
          <button onClick={() => { setForm(null); setError(''); }} style={btn('#f1ece5', '#7a6b5d')}>{form.readOnly ? '← Back' : 'Cancel'}</button>
          {!form.readOnly && <button onClick={save} disabled={saving} style={btn(saving ? '#ccc' : `linear-gradient(135deg,${ACCENT} 0%,#b06e2f 100%)`)}>{saving ? 'Saving…' : 'Save Discount'}</button>}
        </div>
      </div>
    </div>
  );
}
